import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  DatePicker,
  Spin,
  Typography,
  Badge,
  Tabs,
  Space,
  Divider,
  Alert,
  Timeline,
  Statistic,
  Progress,
  Empty,
} from "antd";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import styled from "styled-components";
import { useQuery } from "react-query";
import {
  MailOutlined,
  LineChartOutlined,
  WarningOutlined,
  CalendarOutlined,
  UserOutlined,
  RiseOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";

// CSS
import "@assets/css/EmailAnalyticsDashboard.css";

// API
import {
  fetchDashboardStats,
  getMinimalCampaignData,
  getCampaignProgress,
  resumeEmailCampaign,
} from "@api/email/emailCampaign";

// Campaign Progress Card
import { CampaignProgressCard, ScheduledCampaignCard } from "@components";

// Sequence Components
import { SequenceAnalyticsTab } from "./emailSequences/components";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Add these constants at the top with other imports
const CAMPAIGN_REFRESH_INTERVAL = 5000; // 5 seconds
const CAMPAIGN_STALE_TIME = 3000; // 3 seconds

// Styled Components
const StyledCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .ant-card-head-title {
    font-weight: 600;
  }
`;

// Motion Components
const MetricCard = styled(motion.div)`
  padding: 24px;
  border-radius: 12px;
  background: ${(props) => props.background || "#fff"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: 100%;

  .metric-title {
    color: rgba(0, 0, 0, 0.45);
    font-size: 14px;
    margin-bottom: 8px;
  }

  .metric-value {
    font-size: 32px;
    font-weight: 600;
    color: ${(props) => props.valueColor || "rgba(0, 0, 0, 0.85)"};
    margin-bottom: 8px;
  }

  .metric-trend {
    font-size: 14px;
  }
`;

//  Chart Container
const ChartContainer = styled.div`
  height: 400px;
  width: 100%;
`;

const EmailAnalyticsDashboard = () => {
  const history = useHistory();
  const [timeRange, setTimeRange] = useState("7d");
  const [customDateRange, setCustomDateRange] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [sendingCampaigns, setSendingCampaigns] = useState(new Set());

  // Replace the upcomingCampaigns query with this enhanced version
  const {
    data: campaignData,
    isLoading: upcomingLoading,
    refetch: refetchCampaigns,
  } = useQuery(
    ["upcomingCampaigns"],
    async () => {
      const response = await getMinimalCampaignData();
      return response.data;
    },
    {
      refetchInterval: CAMPAIGN_REFRESH_INTERVAL,
      refetchIntervalInBackground: true,
      staleTime: CAMPAIGN_STALE_TIME,
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (!upcomingLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [upcomingLoading]);

  // Add this effect to track sending campaigns
  useEffect(() => {
    if (campaignData?.campaigns) {
      const sendingIds = new Set(
        campaignData.campaigns
          .filter((c) => c.status === "sending")
          .map((c) => c._id)
      );
      setSendingCampaigns(sendingIds);
    }
  }, [campaignData]);

  // Add progress query for sending campaigns
  const { data: progressData } = useQuery(
    ["campaignProgress", Array.from(sendingCampaigns)],
    async () => {
      const progressPromises = Array.from(sendingCampaigns).map((id) =>
        getCampaignProgress(id).then((response) => ({
          id,
          ...response.data,
          lastUpdated: Date.now(),
        }))
      );
      return Promise.all(progressPromises).then((results) =>
        results.reduce((acc, result) => {
          acc[result.id] = result;
          return acc;
        }, {})
      );
    },
    {
      enabled: sendingCampaigns.size > 0,
      refetchInterval: CAMPAIGN_REFRESH_INTERVAL,
      refetchIntervalInBackground: true,
      staleTime: CAMPAIGN_STALE_TIME,
      keepPreviousData: true,
    }
  );

  // React Query hooks
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery(
    ["emailStats", timeRange, customDateRange, selectedCampaign],
    async () => {
      let params = {};

      if (
        timeRange === "custom" &&
        customDateRange?.[0] &&
        customDateRange?.[1]
      ) {
        params = {
          dateFrom: customDateRange[0].startOf("day").toISOString(),
          dateTo: customDateRange[1].endOf("day").toISOString(),
        };
      } else {
        params = { period: timeRange };
      }

      if (selectedCampaign) {
        params.campaignId = selectedCampaign;
      }

      const statsResponse = await fetchDashboardStats(params);
      return statsResponse.data;
    },
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      staleTime: 20000,
      keepPreviousData: true,
    }
  );

  // Format seconds to display
  const formatTimeRemaining = (seconds) => {
    if (!seconds && seconds !== 0) return "Calculating...";
    if (seconds === 0) return "Almost done...";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0)
      parts.push(`${remainingSeconds}s`);

    return parts.join(" ");
  };

  if (statsError) {
    return (
      <Alert
        message="Error Loading Dashboard"
        description={statsError.message}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  // Get total stats
  const getTotalStats = () => {
    const totalEmailsSent = stats?.data?.overallStats?.totalEmailsSent || 0;
    const openedCount = stats?.data?.overallStats?.openedCount || 0;
    const clickedCount = stats?.data?.overallStats?.clickedCount || 0;
    const bouncedCount = stats?.data?.overallStats?.bouncedCount || 0;

    // Calculate rates directly if not provided by the API or if they are zero with non-zero counts
    const apiOpenRate = stats?.data?.openRate || "0.00";
    const apiClickRate = stats?.data?.clickRate || "0.00";
    const apiBounceRate = stats?.data?.bounceRate || "0.00";

    // If API rates are "0.00" but we have non-zero counts, calculate them
    const openRate =
      apiOpenRate === "0.00" && openedCount > 0 && totalEmailsSent > 0
        ? ((openedCount / totalEmailsSent) * 100).toFixed(2)
        : apiOpenRate;

    const clickRate =
      apiClickRate === "0.00" && clickedCount > 0 && totalEmailsSent > 0
        ? ((clickedCount / totalEmailsSent) * 100).toFixed(2)
        : apiClickRate;

    const bounceRate =
      apiBounceRate === "0.00" && bouncedCount > 0 && totalEmailsSent > 0
        ? ((bouncedCount / totalEmailsSent) * 100).toFixed(2)
        : apiBounceRate;

    return {
      totalEmailsSent,
      openedCount,
      clickedCount,
      bouncedCount,
      openRate,
      clickRate,
      bounceRate,
    };
  };

  // Prepare chart data
  const prepareTimeSeriesData = () => {
    if (!stats?.data?.dailyStats) return [];

    return stats.data.dailyStats.map((stat) => ({
      date: moment(
        new Date(stat._id.year, stat._id.month - 1, stat._id.day)
      ).format("MMM DD"),
      Sent: stat.totalSent || 0,
      Opened: stat.openedCount || 0,
      Clicked: stat.clickedCount || 0,
    }));
  };

  // Calculate rates properly
  const calculateRate = (numerator, denominator) => {
    if (!denominator) return 0;
    return ((numerator / denominator) * 100).toFixed(1);
  };

  // Prepare Campaign Performance Data
  const prepareCampaignPerformance = () => {
    const totals = getTotalStats();
    return [
      {
        type: "Sent",
        value: totals.totalEmailsSent,
      },
      {
        type: "Opened",
        value: totals.openedCount,
      },
      {
        type: "Clicked",
        value: totals.clickedCount,
      },
      {
        type: "Bounced",
        value: totals.bouncedCount,
      },
    ];
  };

  // Chart Configurations
  const timeSeriesConfig = {
    data: prepareTimeSeriesData(),
    xField: "date",
    yField: "value",
    seriesField: "category",
    smooth: true,
    isStack: false,
    areaStyle: {
      fillOpacity: 0.3,
    },
    animation: {
      appear: {
        animation: "wave-in",
        duration: 1000,
        height: 350,
        autoFit: true,
        padding: [30, 30, 50, 50],
      },
    },
  };

  // Campaign Performance Config
  const performanceConfig = {
    data: prepareCampaignPerformance(),
    xField: "type",
    yField: "value",
    seriesField: "type",
    color: ["#1890FF", "#52C41A", "#FAAD14", "#FF4D4F"],
    label: {
      position: "top",
      style: {
        fill: "#000000",
        opacity: 0.6,
      },
    },
    columnStyle: {
      radius: [8, 8, 0, 0],
    },
    animation: {
      appear: {
        animation: "wave-in",
        duration: 1000,
        height: 350,
        autoFit: true,
        padding: [30, 30, 50, 50],
      },
    },
  };

  // Add these helper functions at the top of the file, after the imports
  const getOpenRateStatus = (stats) => {
    if (!stats.openedCount || !stats.totalEmailsSent) return "warning";
    // Use API-provided rate first, but if it's zero and we have opens, calculate it
    const openRate =
      parseFloat(stats.openRate) ||
      (stats.openedCount / stats.totalEmailsSent) * 100;
    return openRate > 21.5 ? "success" : "warning";
  };

  const getOpenRateText = (stats) => {
    if (!stats.openedCount || !stats.totalEmailsSent) return "No data";
    // Use API-provided rate first, but if it's zero and we have opens, calculate it
    const openRate =
      parseFloat(stats.openRate) ||
      (stats.openedCount / stats.totalEmailsSent) * 100;
    return openRate > 21.5 ? "Above Average" : "Below Average";
  };

  // Add these helper functions alongside the other helpers
  const getClickRateStatus = (stats) => {
    if (!stats.clickedCount || !stats.totalEmailsSent) return "warning";
    // Use API-provided rate first, but if it's zero and we have clicks, calculate it
    const clickRate =
      parseFloat(stats.clickRate) ||
      (stats.clickedCount / stats.totalEmailsSent) * 100;
    return clickRate > 2.3 ? "success" : "warning";
  };

  const getClickRateType = (stats) => {
    if (!stats.clickedCount || !stats.totalEmailsSent) return "warning";
    // Use API-provided rate first, but if it's zero and we have clicks, calculate it
    const clickRate =
      parseFloat(stats.clickRate) ||
      (stats.clickedCount / stats.totalEmailsSent) * 100;
    return clickRate > 2.3 ? "success" : "warning";
  };

  // Show initial loading state
  if (statsLoading && !stats) {
    return (
      <div className="EmailDashboard-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "70vh",
            padding: "64px",
          }}
        >
          {/* Animated Email Icons */}
          <motion.div
            style={{
              display: "flex",
              gap: "24px",
              marginBottom: "48px",
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            {[
              { icon: <MailOutlined />, color: "#1890FF", delay: 0 },
              { icon: <ThunderboltOutlined />, color: "#52C41A", delay: 0.1 },
              { icon: <LineChartOutlined />, color: "#FAAD14", delay: 0.2 },
              { icon: <TeamOutlined />, color: "#722ED1", delay: 0.3 },
              { icon: <RiseOutlined />, color: "#FF4D4F", delay: 0.4 },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${item.color}15 0%, ${item.color}30 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  color: item.color,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
                animate={{
                  y: [0, -12, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  delay: item.delay,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {item.icon}
              </motion.div>
            ))}
          </motion.div>

          {/* Spinner with Gradient Ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ marginBottom: "32px" }}
          >
            <div style={{ position: "relative", width: "80px", height: "80px" }}>
              <Spin
                size="large"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <motion.div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  border: "3px solid transparent",
                  borderTopColor: "#1890ff",
                  borderRightColor: "#52c41a",
                  opacity: 0.3,
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
            </div>
          </motion.div>

          {/* Loading Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            style={{ textAlign: "center" }}
          >
            <Title
              level={3}
              style={{
                margin: 0,
                marginBottom: "12px",
                background: "linear-gradient(135deg, #1890ff 0%, #722ed1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: 600,
              }}
            >
              Loading Email Analytics
            </Title>
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: "16px",
                  display: "block",
                }}
              >
                Gathering insights from all your email campaigns...
              </Text>
            </motion.div>
          </motion.div>

          {/* Progress Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            style={{
              marginTop: "48px",
              display: "flex",
              gap: "32px",
              alignItems: "center",
            }}
          >
            {[
              { icon: <MailOutlined />, label: "Analyzing Campaigns" },
              { icon: <LineChartOutlined />, label: "Processing Metrics" },
              { icon: <RiseOutlined />, label: "Calculating Rates" },
            ].map((item, index) => (
              <motion.div
                key={index}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.4,
                  delay: 1 + index * 0.2,
                  type: "spring",
                  stiffness: 200,
                }}
              >
                <motion.div
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 3,
                    delay: index * 0.3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    fontSize: "24px",
                    color: "#1890ff",
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #1890ff15 0%, #1890ff30 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </motion.div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {item.label}
                </Text>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="EmailDashboard-container">
      {/* Header Section */}
      <div className="EmailDashboard-header">
        <Title level={2} className="EmailDashboard-title">
          <MailOutlined />
          Email Analytics Dashboard
        </Title>
        <div className="EmailDashboard-controls">
          <div className="EmailDashboard-controls-group">
            {stats?.data?.campaigns && (
              <Select
                placeholder="Filter by Campaign"
                allowClear
                style={{ minWidth: 200 }}
                value={selectedCampaign}
                onChange={setSelectedCampaign}
                optionLabelProp="label"
              >
                {stats.data.campaigns.map((campaign) => (
                  <Select.Option
                    key={campaign._id || Math.random()}
                    value={campaign._id}
                    label={campaign.name || "Untitled Campaign"}
                  >
                    <Space>
                      <span>{campaign.name || "Untitled Campaign"}</span>
                      <Badge
                        count={campaign.totalSent || 0}
                        style={{
                          backgroundColor:
                            campaign.status === "completed"
                              ? "#52c41a"
                              : "#1890ff",
                        }}
                        overflowCount={9999}
                      />
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            )}

            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ minWidth: 150 }}
              suffixIcon={<CalendarOutlined />}
            >
              <Select.Option value="1d">Last 24 Hours</Select.Option>
              <Select.Option value="7d">Last 7 Days</Select.Option>
              <Select.Option value="30d">Last 30 Days</Select.Option>
              <Select.Option value="custom">Custom Range</Select.Option>
            </Select>

            {timeRange === "custom" && (
              <RangePicker
                value={customDateRange}
                onChange={setCustomDateRange}
                style={{ minWidth: 280 }}
              />
            )}
          </div>

          <div className="EmailDashboard-actions">
            <Button
              type="default"
              icon={<UserOutlined />}
              onClick={() => history.push("/super-admin/email/users")}
            >
              Manage Users
            </Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={() => history.push("/super-admin/email/email-campaigns")}
            >
              View Campaigns
            </Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={() => history.push("/super-admin/email/email-sequences")}
            >
              View Sequences
            </Button>
          </div>
        </div>
      </div>

      {/* Selected Campaign Info */}
      {selectedCampaign && stats?.data?.campaigns && (
        <Row style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Alert
              message={
                <Space>
                  <span>Showing stats for campaign:</span>
                  <Text strong>
                    {
                      stats.data.campaigns.find(
                        (c) => c._id === selectedCampaign
                      )?.name
                    }
                  </Text>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => setSelectedCampaign(null)}
                  >
                    Clear Filter
                  </Button>
                </Space>
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>
      )}

      {/* Main Tabs */}
      <Tabs
        defaultActiveKey="campaigns"
        size="large"
        style={{ marginTop: "20px" }}
      >
        <TabPane
          tab={
            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <MailOutlined style={{ marginLeft: 10 }} />
              Campaign Analytics
            </span>
          }
          key="campaigns"
        >
          {/* Dashboard Content */}
          {stats && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* Key Metrics */}
              <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <MetricCard
                    background="linear-gradient(135deg, #1890FF 0%, #096DD9 100%)"
                    valueColor="#fff"
                    animate={{ y: [50, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.5 }}
                  >
                    <Text
                      className="metric-title"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Total Emails Sent
                    </Text>
                    <div className="metric-value" style={{ color: "#fff" }}>
                      {getTotalStats().totalEmailsSent.toLocaleString()}
                    </div>
                    <div
                      className="metric-trend"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      <RiseOutlined /> Active Now
                    </div>
                  </MetricCard>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <MetricCard
                    background="linear-gradient(135deg, #52C41A 0%, #389E0D 100%)"
                    valueColor="#fff"
                    animate={{ y: [50, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <Text
                      className="metric-title"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Open Rate
                    </Text>
                    <div className="metric-value" style={{ color: "#fff" }}>
                      {getTotalStats().openRate}%
                    </div>
                    <div
                      className="metric-trend"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      <Badge
                        status={getOpenRateStatus(getTotalStats())}
                        text={<Text>{getOpenRateText(getTotalStats())}</Text>}
                      />
                    </div>
                  </MetricCard>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <MetricCard
                    background="linear-gradient(135deg, #FAAD14 0%, #D48806 100%)"
                    valueColor="#fff"
                    animate={{ y: [50, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Text
                      className="metric-title"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Click Rate
                    </Text>
                    <div className="metric-value" style={{ color: "#fff" }}>
                      {getTotalStats().clickRate}%
                    </div>
                    <div
                      className="metric-trend"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      <Badge
                        status={
                          Number(getTotalStats().clickRate) > 2.3
                            ? "success"
                            : "warning"
                        }
                        text={
                          <span style={{ color: "#fff" }}>
                            Industry Avg: 2.3%
                          </span>
                        }
                      />
                    </div>
                  </MetricCard>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <MetricCard
                    background="linear-gradient(135deg, #FF4D4F 0%, #CF1322 100%)"
                    valueColor="#fff"
                    animate={{ y: [50, 0], opacity: [0, 1] }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Text
                      className="metric-title"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Bounce Rate
                    </Text>
                    <div className="metric-value" style={{ color: "#fff" }}>
                      {getTotalStats().bounceRate}%
                    </div>
                    <div
                      className="metric-trend"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      <Badge
                        status={
                          Number(getTotalStats().bounceRate) < 0.7
                            ? "success"
                            : "warning"
                        }
                        text={
                          <span style={{ color: "#fff" }}>
                            Industry Avg: 0.7%
                          </span>
                        }
                      />
                    </div>
                  </MetricCard>
                </Col>
              </Row>

              {/* Upcoming Campaigns Section */}
              <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24}>
                  <StyledCard
                    title={
                      <Space>
                        <ClockCircleOutlined />
                        <span>Campaign Overview</span>
                      </Space>
                    }
                    className="EmailDashboard-card no-hover"
                    extra={
                      <Space>
                        {upcomingLoading && <Spin size="small" />}
                        <Badge
                          count={campaignData?.campaignCounts?.total || 0}
                          style={{ backgroundColor: "#1890ff" }}
                          overflowCount={999}
                        />
                      </Space>
                    }
                  >
                    {upcomingLoading ? (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin />
                      </div>
                    ) : (
                      <>
                        {/* Campaign Stats Summary */}
                        {!isInitialLoad && (
                          <>
                            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                              <Col xs={24} sm={12} md={4} lg={4}>
                                <Card bordered={false}>
                                  <Statistic
                                    title="Total Campaigns"
                                    value={
                                      campaignData?.campaignCounts?.total || 0
                                    }
                                    prefix={
                                      <TeamOutlined
                                        style={{ color: "#1890ff" }}
                                      />
                                    }
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={12} md={4} lg={4}>
                                <Card bordered={false}>
                                  <Statistic
                                    title="Scheduled"
                                    value={
                                      campaignData?.campaignCounts?.scheduled ||
                                      0
                                    }
                                    prefix={
                                      <ClockCircleOutlined
                                        style={{ color: "#722ed1" }}
                                      />
                                    }
                                    valueStyle={{ color: "#722ed1" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={12} md={4} lg={4}>
                                <Card bordered={false}>
                                  <Statistic
                                    title="In Progress"
                                    value={
                                      campaignData?.campaignCounts?.sending || 0
                                    }
                                    prefix={
                                      <SyncOutlined
                                        spin
                                        style={{ color: "#52c41a" }}
                                      />
                                    }
                                    valueStyle={{ color: "#52c41a" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={12} md={4} lg={4}>
                                <Card bordered={false}>
                                  <Statistic
                                    title="Paused"
                                    value={
                                      campaignData?.campaignCounts?.paused || 0
                                    }
                                    prefix={
                                      <PauseCircleOutlined
                                        style={{ color: "#fa8c16" }}
                                      />
                                    }
                                    valueStyle={{ color: "#fa8c16" }}
                                  />
                                </Card>
                              </Col>
                              <Col xs={24} sm={12} md={4} lg={4}>
                                <Card bordered={false}>
                                  <Statistic
                                    title="Completed"
                                    value={
                                      campaignData?.campaignCounts?.completed ||
                                      0
                                    }
                                    prefix={
                                      <CheckCircleOutlined
                                        style={{ color: "#1890ff" }}
                                      />
                                    }
                                  />
                                </Card>
                              </Col>
                            </Row>

                            {/* Scheduled Campaigns */}
                            {(campaignData?.scheduledCampaigns?.length > 0 ||
                              campaignData?.campaigns?.some(
                                (c) =>
                                  c.status === "sending" ||
                                  c.status === "paused"
                              )) && (
                              <div>
                                <Title level={4} style={{ marginBottom: 24 }}>
                                  <CalendarOutlined /> Active, Paused &
                                  Scheduled Campaigns
                                </Title>
                                <Row gutter={[24, 24]}>
                                  {/* Show sending campaigns first */}
                                  {campaignData?.campaigns
                                    ?.filter((c) => c.status === "sending")
                                    .map((campaign) => (
                                      <Col
                                        xs={24}
                                        md={12}
                                        lg={8}
                                        key={campaign._id}
                                      >
                                        <motion.div
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -20 }}
                                          transition={{ duration: 0.3 }}
                                        >
                                          <CampaignProgressCard
                                            campaign={campaign}
                                            progress={
                                              progressData?.[campaign._id]
                                            }
                                          />
                                        </motion.div>
                                      </Col>
                                    ))}

                                  {/* Show paused campaigns */}
                                  {campaignData?.campaigns
                                    ?.filter((c) => c.status === "paused")
                                    .map((campaign) => (
                                      <Col
                                        xs={24}
                                        md={12}
                                        lg={8}
                                        key={campaign._id}
                                      >
                                        <motion.div
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -20 }}
                                          transition={{ duration: 0.3 }}
                                        >
                                          <Card
                                            size="small"
                                            style={{
                                              borderLeft: "4px solid #fa8c16",
                                              backgroundColor: "#fff7e6",
                                            }}
                                            title={
                                              <Space>
                                                <PauseCircleOutlined
                                                  style={{ color: "#fa8c16" }}
                                                />
                                                <Text
                                                  strong
                                                  style={{
                                                    color: "#fa8c16",
                                                    fontSize: "14px",
                                                  }}
                                                >
                                                  PAUSED
                                                </Text>
                                              </Space>
                                            }
                                            extra={
                                              <Badge
                                                status="warning"
                                                text="Needs attention"
                                              />
                                            }
                                          >
                                            <Space
                                              direction="vertical"
                                              style={{ width: "100%" }}
                                              size="small"
                                            >
                                              <div>
                                                <Text
                                                  strong
                                                  style={{
                                                    fontSize: "16px",
                                                    display: "block",
                                                    marginBottom: "8px",
                                                  }}
                                                >
                                                  {campaign.name || "Untitled Campaign"}
                                                </Text>
                                                <Text type="secondary">
                                                  {campaign.subject || "No subject"}
                                                </Text>
                                              </div>

                                              {(campaign.statistics?.pausedAt ||
                                                campaign.statistics
                                                  ?.pauseReason) && (
                                                <>
                                                  {campaign.statistics
                                                    ?.pausedAt && (
                                                    <div>
                                                      <Text type="secondary">
                                                        Paused:{" "}
                                                      </Text>
                                                      <Text>
                                                        {moment(
                                                          campaign.statistics
                                                            .pausedAt
                                                        ).format(
                                                          "MMM DD, HH:mm"
                                                        )}
                                                      </Text>
                                                    </div>
                                                  )}

                                                  {campaign.statistics
                                                    ?.pauseReason && (
                                                    <div>
                                                      <Text type="secondary">
                                                        Reason:{" "}
                                                      </Text>
                                                      <Text>
                                                        {
                                                          campaign.statistics
                                                            .pauseReason
                                                        }
                                                      </Text>
                                                    </div>
                                                  )}
                                                </>
                                              )}

                                              <Button
                                                type="primary"
                                                size="small"
                                                icon={<SyncOutlined />}
                                                style={{
                                                  backgroundColor: "#52c41a",
                                                  borderColor: "#52c41a",
                                                  marginTop: "8px",
                                                }}
                                                onClick={async () => {
                                                  try {
                                                    await resumeEmailCampaign(
                                                      campaign._id
                                                    );
                                                    // Refetch campaign data to update the UI
                                                    await refetchCampaigns();
                                                  } catch (error) {
                                                    console.error(
                                                      "Error resuming campaign:",
                                                      error
                                                    );
                                                    // You might want to show a notification here
                                                  }
                                                }}
                                              >
                                                Resume Campaign
                                              </Button>
                                            </Space>
                                          </Card>
                                        </motion.div>
                                      </Col>
                                    ))}

                                  {/* Show scheduled campaigns sorted by date */}
                                  {campaignData?.scheduledCampaigns
                                    ?.filter((campaign) => {
                                      const scheduledTime = new Date(
                                        campaign.scheduledFor
                                      ).getTime();
                                      const now = Date.now();
                                      return scheduledTime > now;
                                    })
                                    .sort(
                                      (a, b) =>
                                        new Date(a.scheduledFor) -
                                        new Date(b.scheduledFor)
                                    )
                                    .map((campaign) => (
                                      <Col
                                        xs={24}
                                        md={12}
                                        lg={8}
                                        key={campaign._id}
                                      >
                                        <motion.div
                                          initial={{ opacity: 0, y: 20 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          exit={{ opacity: 0, y: -20 }}
                                          transition={{ duration: 0.3 }}
                                        >
                                          {/* Replace this Card with the new ScheduledCampaignCard */}
                                          <ScheduledCampaignCard
                                            campaign={campaign}
                                          />
                                        </motion.div>
                                      </Col>
                                    ))}
                                </Row>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </StyledCard>
                </Col>
              </Row>

              {/* Charts */}
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={16}>
                  <StyledCard title="Email Engagement Over Time">
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={prepareTimeSeriesData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="Sent"
                            stackId="1"
                            stroke="#1890FF"
                            fill="#1890FF"
                            fillOpacity={0.3}
                          />
                          <Area
                            type="monotone"
                            dataKey="Opened"
                            stackId="2"
                            stroke="#52C41A"
                            fill="#52C41A"
                            fillOpacity={0.3}
                          />
                          <Area
                            type="monotone"
                            dataKey="Clicked"
                            stackId="3"
                            stroke="#FAAD14"
                            fill="#FAAD14"
                            fillOpacity={0.3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </StyledCard>
                </Col>

                {/* Open Rate Performance */}
                <Col xs={24} lg={8}>
                  <StyledCard title="Open Rate Performance">
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "20px",
                      }}
                    >
                      <Progress
                        type="dashboard"
                        percent={Number(getTotalStats().openRate)}
                        strokeColor={{
                          "0%": "#108ee9",
                          "100%": "#87d068",
                        }}
                        width={250}
                        format={(percent) => (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{ fontSize: "24px", fontWeight: "bold" }}
                            >
                              {percent?.toFixed(1)}%
                            </span>
                            <span
                              style={{ fontSize: "14px", color: "#8c8c8c" }}
                            >
                              Open Rate
                            </span>
                          </div>
                        )}
                      />

                      <Row
                        gutter={[24, 0]}
                        style={{
                          width: "100%",
                          marginTop: "24px",
                          textAlign: "center",
                        }}
                      >
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Total Opens</Text>
                            <div
                              style={{ fontSize: "24px", fontWeight: "bold" }}
                            >
                              {getTotalStats().openedCount}
                            </div>
                          </div>
                        </Col>
                        <Col span={12}>
                          <div>
                            <Text type="secondary">Unique Opens</Text>
                            <div
                              style={{ fontSize: "24px", fontWeight: "bold" }}
                            >
                              {stats.analytics?.opens?.timeDistribution?.NaN ||
                                0}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Divider style={{ margin: "24px 0" }} />

                      <div style={{ width: "100%" }}>
                        <Text
                          strong
                          style={{ display: "block", marginBottom: "16px" }}
                        >
                          Device Breakdown
                        </Text>
                        {stats?.analytics?.opens?.devices &&
                        Object.keys(stats.analytics.opens.devices).length >
                          0 ? (
                          Object.entries(stats.analytics.opens.devices).map(
                            ([device, count]) => (
                              <div
                                key={device}
                                style={{ marginBottom: "16px" }}
                              >
                                <Space
                                  justify="space-between"
                                  style={{ width: "100%", marginBottom: "4px" }}
                                >
                                  <Text>{device}</Text>
                                  <Text>{count}</Text>
                                </Space>
                                <Progress
                                  percent={Number(
                                    (
                                      (count / getTotalStats().openedCount) *
                                      100
                                    ).toFixed(1)
                                  )}
                                  strokeColor="#108ee9"
                                  size="small"
                                />
                              </div>
                            )
                          )
                        ) : (
                          <Empty description="No device data available" />
                        )}
                      </div>

                      <div style={{ marginTop: "24px", textAlign: "center" }}>
                        <Text type="secondary" style={{ fontSize: "16px" }}>
                          Industry Average: 21.5%
                        </Text>
                        <br />
                        <Badge
                          status={getOpenRateStatus(getTotalStats())}
                          text={<Text>{getOpenRateText(getTotalStats())}</Text>}
                        />
                      </div>
                    </div>
                  </StyledCard>
                </Col>

                {/* Add this after the Open Rate Performance StyledCard in your Row */}
                <Col xs={24} lg={8}>
                  <StyledCard title="Email Performance Metrics">
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <div style={{ textAlign: "center" }}>
                        <Progress
                          type="dashboard"
                          percent={Number(getTotalStats().clickRate)}
                          strokeColor={{
                            "0%": "#faad14",
                            "100%": "#d48806",
                          }}
                          width={200}
                          format={(percent) => (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{ fontSize: "24px", fontWeight: "bold" }}
                              >
                                {percent?.toFixed(1)}%
                              </span>
                              <span
                                style={{ fontSize: "14px", color: "#8c8c8c" }}
                              >
                                Click Rate
                              </span>
                            </div>
                          )}
                        />

                        <div style={{ marginTop: 16 }}>
                          <Row gutter={[16, 16]}>
                            <Col span={8}>
                              <Statistic
                                title="Total Clicks"
                                value={getTotalStats().clickedCount}
                                valueStyle={{ fontSize: "16px" }}
                              />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="Failed"
                                value={
                                  stats?.data?.overallStats?.failedCount || 0
                                }
                                valueStyle={{
                                  fontSize: "16px",
                                  color:
                                    stats?.data?.overallStats?.failedCount > 0
                                      ? "#ff4d4f"
                                      : undefined,
                                }}
                              />
                            </Col>
                            <Col span={8}>
                              <Statistic
                                title="Delivered"
                                value={
                                  stats?.data?.overallStats?.deliveredCount || 0
                                }
                                valueStyle={{ fontSize: "16px" }}
                              />
                            </Col>
                          </Row>
                        </div>
                      </div>

                      {/* Benchmarks */}
                      <div style={{ marginTop: 20 }}>
                        <Alert
                          message={
                            <Space align="center">
                              <Badge
                                status={getClickRateStatus(getTotalStats())}
                              />
                              <Text>Click Rate vs Industry (2.3%)</Text>
                            </Space>
                          }
                          type={getClickRateType(getTotalStats())}
                          showIcon={false}
                        />
                      </div>
                    </Space>
                  </StyledCard>
                </Col>

                {/* Add this after the Campaign Statistics StyledCard in your Row */}
                <Col xs={24}>
                  <StyledCard
                    title={
                      <Space>
                        <MailOutlined />
                        <span>Sender Statistics</span>
                      </Space>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      {stats?.data?.senderStats?.map((sender) => (
                        <Col xs={24} sm={12} lg={8} key={sender.email || Math.random()}>
                          <Card>
                            <Space
                              direction="vertical"
                              style={{ width: "100%" }}
                              size="small"
                            >
                              <Space align="baseline">
                                <Text strong>{sender.email || "Unknown"}</Text>
                                <Badge
                                  count={sender.totalSent || 0}
                                  style={{ backgroundColor: "#1890ff" }}
                                  overflowCount={9999}
                                />
                              </Space>

                              <Row gutter={16}>
                                <Col span={12}>
                                  <Statistic
                                    title="Delivered"
                                    value={sender.delivered || 0}
                                    valueStyle={{ fontSize: "16px" }}
                                  />
                                </Col>
                                <Col span={12}>
                                  <Statistic
                                    title="Failed"
                                    value={sender.failed || 0}
                                    valueStyle={{
                                      fontSize: "16px",
                                      color:
                                        (sender.failed || 0) > 0
                                          ? "#ff4d4f"
                                          : undefined,
                                    }}
                                  />
                                </Col>
                              </Row>

                              <Space
                                direction="vertical"
                                style={{ width: "100%" }}
                              >
                                <div>
                                  <Text type="secondary">Delivery Rate</Text>
                                  <Progress
                                    percent={Number(sender.deliveryRate) || 0}
                                    size="small"
                                    strokeColor="#1890ff"
                                  />
                                </div>
                                <div>
                                  <Text type="secondary">Open Rate</Text>
                                  <Progress
                                    percent={Number(sender.openRate) || 0}
                                    size="small"
                                    strokeColor="#52c41a"
                                  />
                                </div>
                                <div>
                                  <Text type="secondary">Click Rate</Text>
                                  <Progress
                                    percent={Number(sender.clickRate) || 0}
                                    size="small"
                                    strokeColor="#faad14"
                                  />
                                </div>
                              </Space>
                            </Space>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </StyledCard>
                </Col>

                {/* Click Analytics Overview */}
                <Col xs={24}>
                  <StyledCard
                    title={
                      <Space>
                        <LineChartOutlined />
                        <span>Click Analytics Overview</span>
                      </Space>
                    }
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} sm={12} md={6}>
                        <Card bordered={false}>
                          <Statistic
                            title="Total Clicks"
                            value={getTotalStats().clickedCount}
                            prefix={
                              <ThunderboltOutlined
                                style={{ color: "#1890ff" }}
                              />
                            }
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card bordered={false}>
                          <Statistic
                            title="Unique Clickers"
                            value={
                              stats?.clickAnalytics?.clickTimings
                                ?.uniqueClickers ||
                              stats?.data?.overallStats?.clickedCount ||
                              0
                            }
                            prefix={
                              <UserOutlined style={{ color: "#52c41a" }} />
                            }
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card bordered={false}>
                          <Statistic
                            title="Avg. Clicks per User"
                            value={Number(
                              stats?.clickAnalytics?.clickTimings
                                ?.averageClicksPerUser || 1
                            ).toFixed(2)}
                            prefix={
                              <LineChartOutlined style={{ color: "#faad14" }} />
                            }
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card bordered={false}>
                          <Statistic
                            title="Click Through Rate"
                            value={`${getTotalStats().clickRate}%`}
                            prefix={
                              <RiseOutlined style={{ color: "#722ed1" }} />
                            }
                          />
                        </Card>
                      </Col>
                    </Row>
                  </StyledCard>
                </Col>

                {/* Link Categories Analysis */}
                <Col xs={24} lg={12}>
                  <StyledCard
                    title={
                      <Space>
                        <LineChartOutlined />
                        <span>Link Categories</span>
                      </Space>
                    }
                  >
                    <Space
                      direction="vertical"
                      style={{ width: "100%" }}
                      size="large"
                    >
                      {stats?.clickAnalytics?.linkAnalysis?.categories &&
                      Object.keys(stats.clickAnalytics.linkAnalysis.categories)
                        .length > 0 ? (
                        Object.entries(
                          stats.clickAnalytics.linkAnalysis.categories
                        ).map(([category, count]) => (
                          <div key={category || Math.random()}>
                            <Space
                              justify="space-between"
                              style={{ width: "100%", marginBottom: 8 }}
                            >
                              <Text>{(category || "unknown").toUpperCase()}</Text>
                              <Text strong>{count || 0}</Text>
                            </Space>
                            <Progress
                              percent={
                                (count / getTotalStats().clickedCount) * 100 ||
                                0
                              }
                              strokeColor={
                                category === "cta"
                                  ? "#1890ff"
                                  : category === "product"
                                  ? "#52c41a"
                                  : category === "social"
                                  ? "#faad14"
                                  : category === "resource"
                                  ? "#722ed1"
                                  : "#f5222d"
                              }
                            />
                          </div>
                        ))
                      ) : (
                        <Empty description="No link category data available" />
                      )}
                    </Space>
                  </StyledCard>
                </Col>

                {/* Device Analytics */}
                <Col xs={24} lg={12}>
                  <StyledCard
                    title={
                      <Space>
                        <UserOutlined />
                        <span>Device & Location Analytics</span>
                      </Space>
                    }
                  >
                    <Tabs defaultActiveKey="devices">
                      <Tabs.TabPane tab="Devices" key="devices">
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size="large"
                        >
                          {stats?.clickAnalytics?.deviceAnalysis?.devices &&
                          Object.keys(
                            stats.clickAnalytics.deviceAnalysis.devices
                          ).length > 0 ? (
                            Object.entries(
                              stats.clickAnalytics.deviceAnalysis.devices
                            ).map(([device, count]) => (
                              <div key={device}>
                                <Space
                                  justify="space-between"
                                  style={{ width: "100%", marginBottom: 8 }}
                                >
                                  <Text>{device || "Unknown"}</Text>
                                  <Text strong>{count}</Text>
                                </Space>
                                <Progress
                                  percent={
                                    (count / getTotalStats().clickedCount) *
                                      100 || 0
                                  }
                                  strokeColor="#1890ff"
                                />
                              </div>
                            ))
                          ) : (
                            <Empty description="No device data available" />
                          )}
                        </Space>
                      </Tabs.TabPane>
                      <Tabs.TabPane tab="Email Clients" key="clients">
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size="large"
                        >
                          {stats?.clickAnalytics?.deviceAnalysis
                            ?.emailClients &&
                          Object.keys(
                            stats.clickAnalytics.deviceAnalysis.emailClients
                          ).length > 0 ? (
                            Object.entries(
                              stats.clickAnalytics.deviceAnalysis.emailClients
                            ).map(([client, count]) => (
                              <div key={client}>
                                <Space
                                  justify="space-between"
                                  style={{ width: "100%", marginBottom: 8 }}
                                >
                                  <Text>{client || "Unknown"}</Text>
                                  <Text strong>{count}</Text>
                                </Space>
                                <Progress
                                  percent={
                                    (count / getTotalStats().clickedCount) *
                                      100 || 0
                                  }
                                  strokeColor="#52c41a"
                                />
                              </div>
                            ))
                          ) : (
                            <Empty description="No email client data available" />
                          )}
                        </Space>
                      </Tabs.TabPane>
                      <Tabs.TabPane tab="Countries" key="countries">
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size="large"
                        >
                          {stats?.clickAnalytics?.deviceAnalysis?.countries &&
                          Object.keys(
                            stats.clickAnalytics.deviceAnalysis.countries
                          ).length > 0 ? (
                            Object.entries(
                              stats.clickAnalytics.deviceAnalysis.countries
                            ).map(([country, count]) => {
                              const percentage = Number(
                                (
                                  (count / getTotalStats().clickedCount) *
                                  100
                                ).toFixed(1)
                              );
                              return (
                                <div key={country}>
                                  <Space
                                    justify="space-between"
                                    style={{ width: "100%", marginBottom: 8 }}
                                  >
                                    <Text>{country || "Unknown"}</Text>
                                    <Text strong>{count}</Text>
                                  </Space>
                                  <Progress
                                    percent={percentage}
                                    strokeColor="#faad14"
                                    size="small"
                                  />
                                </div>
                              );
                            })
                          ) : (
                            <Empty description="No country data available" />
                          )}
                        </Space>
                      </Tabs.TabPane>
                    </Tabs>
                  </StyledCard>
                </Col>

                {/* Timing Analysis */}
                <Col xs={24}>
                  <StyledCard
                    title={
                      <Space>
                        <ClockCircleOutlined />
                        <span>Click Timing Analysis</span>
                      </Space>
                    }
                  >
                    <Row gutter={[24, 24]}>
                      <Col xs={24} md={12}>
                        <Card bordered={false}>
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Text type="secondary">First Click</Text>
                            <Text strong>
                              {stats?.clickAnalytics?.clickTimings?.firstClick
                                ? moment(
                                    stats.clickAnalytics.clickTimings.firstClick
                                  ).format("MMMM Do YYYY, h:mm:ss a")
                                : "No clicks yet"}
                            </Text>
                          </Space>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <Card bordered={false}>
                          <Space direction="vertical" style={{ width: "100%" }}>
                            <Text type="secondary">Last Click</Text>
                            <Text strong>
                              {stats.clickAnalytics?.clickTimings?.lastClick
                                ? moment(
                                    stats.clickAnalytics.clickTimings.lastClick
                                  ).format("MMMM Do YYYY, h:mm:ss a")
                                : "No clicks yet"}
                            </Text>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </StyledCard>
                </Col>

                <Col xs={24} lg={12}>
                  <StyledCard title="Campaign Performance">
                    <ChartContainer>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={prepareCampaignPerformance()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="type" />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="value"
                            fill="#1890FF"
                            radius={[8, 8, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </StyledCard>
                </Col>
                <Col xs={24} lg={12}>
                  <StyledCard title="Campaign Statistics">
                    <Row gutter={[24, 24]}>
                      <Col span={8}>
                        <Statistic
                          title="Total Campaigns"
                          value={stats?.data?.totalCampaignsAllTime || 0}
                          prefix={<LineChartOutlined />}
                        />
                      </Col>

                      <Col span={8}>
                        <Statistic
                          title="Active Campaigns"
                          value={stats?.data?.campaignsInRange || 0}
                          prefix={
                            <ThunderboltOutlined style={{ color: "#52C41A" }} />
                          }
                          valueStyle={{ color: "#52C41A" }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title="Failed Deliveries"
                          value={stats?.data?.overallStats?.failedCount || 0}
                          prefix={
                            <WarningOutlined style={{ color: "#FF4D4F" }} />
                          }
                          valueStyle={{ color: "#FF4D4F" }}
                        />
                      </Col>
                    </Row>

                    {/* Campaign Health Indicators */}
                    <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                      <Col span={24}>
                        <Card
                          bordered={false}
                          bodyStyle={{ padding: "12px 0" }}
                        >
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                            size="middle"
                          >
                            <div>
                              <Text type="secondary">
                                Delivery Success Rate
                              </Text>
                              <Progress
                                percent={Number(
                                  stats?.data?.overallStats?.deliveredCount
                                    ? (stats.data.overallStats.deliveredCount /
                                        getTotalStats().totalEmailsSent) *
                                        100
                                    : 100
                                ).toFixed(1)}
                                strokeColor={{
                                  "0%": "#108ee9",
                                  "100%": "#87d068",
                                }}
                              />
                            </div>
                            <div>
                              <Text type="secondary">Engagement Rate</Text>
                              <Progress
                                percent={Number(getTotalStats().openRate)}
                                strokeColor={{
                                  from: "#108ee9",
                                  to: "#87d068",
                                }}
                              />
                            </div>
                            <div>
                              <Text type="secondary">Click-through Rate</Text>
                              <Progress
                                percent={Number(getTotalStats().clickRate)}
                                strokeColor={{
                                  "0%": "#ffd700",
                                  "100%": "#fa8c16",
                                }}
                              />
                            </div>
                          </Space>
                        </Card>
                      </Col>
                    </Row>
                  </StyledCard>
                </Col>

                {/* Additional Insights Section */}
                <Col xs={24}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} lg={8}>
                      <StyledCard
                        title={
                          <Space>
                            <RiseOutlined />
                            <span>Quick Insights</span>
                          </Space>
                        }
                      >
                        <Space
                          direction="vertical"
                          style={{ width: "100%" }}
                          size="large"
                        >
                          <Alert
                            message="Open Rate Trend"
                            description={`Your open rate of ${
                              getTotalStats().openRate
                            }% is ${
                              Number(getTotalStats().openRate) > 21.5
                                ? "above"
                                : "below"
                            } the industry average of 21.5%`}
                            type={
                              Number(getTotalStats().openRate) > 21.5
                                ? "success"
                                : "warning"
                            }
                            showIcon
                          />

                          <Alert
                            message="Delivery Performance"
                            description={`Your bounce rate of ${
                              getTotalStats().bounceRate
                            }% is ${
                              Number(getTotalStats().bounceRate) < 0.7
                                ? "within"
                                : "above"
                            } acceptable limits`}
                            type={
                              Number(getTotalStats().bounceRate) < 0.7
                                ? "success"
                                : "warning"
                            }
                            showIcon
                          />

                          <Alert
                            message="Engagement Quality"
                            description={`Click-through rate of ${
                              getTotalStats().clickRate
                            }% indicates ${
                              Number(getTotalStats().clickRate) > 2.3
                                ? "strong"
                                : "moderate"
                            } content engagement`}
                            type={
                              Number(getTotalStats().clickRate) > 2.3
                                ? "success"
                                : "info"
                            }
                            showIcon
                          />
                        </Space>
                      </StyledCard>
                    </Col>

                    <Col xs={24} lg={16}>
                      <StyledCard
                        title={
                          <Space>
                            <ThunderboltOutlined />
                            <span>Recommendations</span>
                          </Space>
                        }
                      >
                        <Timeline mode="left">
                          {Number(getTotalStats().openRate) < 21.5 && (
                            <Timeline.Item
                              color="blue"
                              label="Improve Open Rates"
                            >
                              <Text strong>Optimize Subject Lines</Text>
                              <br />
                              <Text type="secondary">
                                Consider A/B testing subject lines and sending
                                times to improve open rates. Current performance
                                is below industry average.
                              </Text>
                            </Timeline.Item>
                          )}

                          {Number(getTotalStats().bounceRate) > 0.7 && (
                            <Timeline.Item color="red" label="Reduce Bounces">
                              <Text strong>Clean Your Email List</Text>
                              <br />
                              <Text type="secondary">
                                High bounce rate detected. Consider validating
                                email addresses and removing inactive
                                subscribers.
                              </Text>
                            </Timeline.Item>
                          )}

                          <Timeline.Item color="green" label="Engagement">
                            <Text strong>Content Strategy</Text>
                            <br />
                            <Text type="secondary">
                              {Number(getTotalStats().clickRate) > 2.3
                                ? "Strong click-through rates. Continue focusing on valuable content."
                                : "Consider personalizing content more to improve engagement."}
                            </Text>
                          </Timeline.Item>
                        </Timeline>
                      </StyledCard>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </motion.div>
          )}
        </TabPane>

        <TabPane
          tab={
            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <SyncOutlined style={{ marginLeft: 10 }} />
              Sequence Analytics
            </span>
          }
          key="sequences"
        >
          <SequenceAnalyticsTab />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default EmailAnalyticsDashboard;
